import { unwrapResult } from "@reduxjs/toolkit";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { DropdownItem, DropdownMenu, DropdownToggle, Modal, Spinner, UncontrolledDropdown } from "reactstrap";
import Loading from "../../../common/Loading";
import ConfirmChangeStatus from "../../../common/modals/ConfirmChangeStatus";
import ConfirmDelete from "../../../common/modals/ConfirmDelete";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
  Icon,
  PaginationComponent,
  TooltipComponent,
  UserAvatar,
} from "../../../components/Component";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import { deleteUser, exportUser, fetchUsers, searchExistingUser, updateUser } from "../../../redux/slices/userSlice";
import { findUpper } from "../../../utils/Utils";

const AllPatients = () => {
  const dispatch = useDispatch();

  const [sm, updateSm] = useState(false);
  const [tablesm, updateTableSm] = useState(false);
  const [onSearch, setonSearch] = useState(true);
  const [onSearchText, setSearchText] = useState(null);
  const [modal, setModal] = useState({
    delete: false,
    status: false,
  });
  const [selectedUser, setSelectedUser] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(10);
  const [sort, setSortState] = useState({ column: "", direction: "" });
  const [statusFilter, setStatusFilter] = useState("all");

  const roles = useSelector((state) => state.role.roles);
  const patients = useSelector((state) => state.user.users);
  const isLoading = useSelector((state) => state.user.isLoading);
  const fetchLoading = useSelector((state) => state.user.fetchLoading);
  const exportLoading = useSelector((state) => state.user.isLoading);

  useEffect(() => {
    if (roles?.data?.find((role) => role?.slug === "user")?.id) {
      if (onSearchText === "" || onSearchText === null) {
        dispatch(
          fetchUsers({
            limit: itemPerPage,
            page: currentPage,
            params: {
              role_id: roles?.data?.find((role) => role?.slug === "user")?.id,

              ...(sort.column !== "" && sort.direction !== ""
                ? {
                    sort: `${sort.column}:${sort.direction}`,
                  }
                : {}),

              ...(statusFilter !== "all"
                ? {
                    status: statusFilter === "active" ? 1 : 0,
                  }
                : {}),
            },
          })
        );
      }
    }
  }, [currentPage, dispatch, itemPerPage, onSearchText, roles?.data, sort, statusFilter]);

  useEffect(() => {
    if (onSearchText !== null) {
      dispatch(
        searchExistingUser({
          useUsers: true,
          params: {
            role_id: roles?.data?.find((role) => role?.slug === "user")?.id,
            ...(onSearchText !== ""
              ? {
                  phone: onSearchText,
                  address: onSearchText,
                }
              : {}),
          },
        })
      );
    }
  }, [dispatch, onSearchText, roles?.data]);

  const toggle = () => setonSearch(!onSearch);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleDelete = async () => {
    try {
      const resultAction = await dispatch(
        deleteUser({ handleClose: () => setModal({ delete: false }), id: selectedUser?.id })
      );
      const originalPromiseResult = unwrapResult(resultAction);
      originalPromiseResult.handleClose && originalPromiseResult.handleClose();
    } catch (error) {}
  };

  const handleChangeStatus = async () => {
    try {
      const resultAction = await dispatch(
        updateUser({
          handleClose: () => setModal({ status: false }),
          id: selectedUser?.id,
          data: {
            status: !selectedUser?.status,
          },
        })
      );
      const originalPromiseResult = unwrapResult(resultAction);
      originalPromiseResult.handleClose && originalPromiseResult.handleClose();
    } catch (error) {}
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [itemPerPage]);

  const handleExportPatients = () => {
    dispatch(
      exportUser({
        role_id: roles?.data?.find((role) => role?.slug === "user")?.id,
        file_name: "patients",
        status: statusFilter !== "all" ? (statusFilter === "active" ? 1 : 0) : undefined,
      })
    );
  };

  const onSortChange = (column) => {
    setSortState((prev) => ({
      column,
      direction: prev.column === column ? (prev.direction === "asc" ? "desc" : "asc") : "asc",
    }));
  };

  return (
    <React.Fragment>
      <Head title="All Patients List"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                All Patients Lists
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>You have total {patients?.meta?.total} patients.</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <Button
                  className={`btn-icon btn-trigger toggle-expand me-n1 ${sm ? "active" : ""}`}
                  onClick={() => updateSm(!sm)}
                >
                  <Icon name="menu-alt-r"></Icon>
                </Button>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <div className="flex justify-end mb-3">
          <Button onClick={handleExportPatients} color={"primary"} className={"flex  items-center"}>
            <Icon
              name={"download"}
              style={{
                marginRight: 3,
              }}
            />
            {exportLoading ? <Spinner size="sm" color="light" /> : "Export"}
          </Button>
        </div>

        {fetchLoading ? (
          <Loading />
        ) : (
          <Block>
            <DataTable className="card-stretch">
              <div className="card-inner position-relative card-tools-toggle">
                <div className="card-title-group flex justify-between">
                  <div className="card-tools">
                    <div className="form-inline flex-nowrap gx-3">
                      <div className="form-wrap w-150px">
                        <select
                          className="form-select js-select2"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="all">All Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>

                      <Button
                        onClick={() => {
                          setStatusFilter("all");
                        }}
                      >
                        RESET
                      </Button>
                    </div>
                  </div>
                  <div className="card-tools me-n1">
                    <ul className="btn-toolbar gx-1">
                      <li>
                        <a
                          href="#search"
                          onClick={(ev) => {
                            ev.preventDefault();
                            toggle();
                          }}
                          className="btn btn-icon search-toggle toggle-search"
                        >
                          <Icon name="search"></Icon>
                        </a>
                      </li>
                      <li className="btn-toolbar-sep"></li>
                      <li>
                        <div className="toggle-wrap">
                          <Button
                            className={`btn-icon btn-trigger toggle ${tablesm ? "active" : ""}`}
                            onClick={() => updateTableSm(true)}
                          >
                            <Icon name="menu-right"></Icon>
                          </Button>
                          <div className={`toggle-content ${tablesm ? "content-active" : ""}`}>
                            <ul className="btn-toolbar gx-1">
                              <li className="toggle-close">
                                <Button className="btn-icon btn-trigger toggle" onClick={() => updateTableSm(false)}>
                                  <Icon name="arrow-left"></Icon>
                                </Button>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className={`card-search search-wrap ${!onSearch && "active"}`}>
                  <div className="card-body">
                    <div className="search-content">
                      <Button
                        className="search-back btn-icon toggle-search active"
                        onClick={() => {
                          setSearchText(null);
                          toggle();
                        }}
                      >
                        <Icon name="arrow-left"></Icon>
                      </Button>
                      <input
                        type="text"
                        className="border-transparent form-focus-none form-control"
                        placeholder="Search by name or email or phone or address"
                        value={onSearchText}
                        onChange={(e) => {
                          setSearchText(e.target.value);
                        }}
                      />
                      <Button className="search-submit btn-icon">
                        <Icon name="search"></Icon>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <DataTableBody compact>
                <DataTableHead>
                  <DataTableRow>
                    <button
                      onClick={() => onSortChange("name")}
                      className="sub-text font-bold cursor-pointer w-full text-start flex justify-between"
                    >
                      <span>Full name</span>{" "}
                      <span>
                        {sort.column === "name" ? (
                          sort.direction === "asc" ? (
                            <Icon name={"sort-down-fill"} />
                          ) : (
                            <Icon name={"sort-up-fill"} />
                          )
                        ) : (
                          <Icon
                            name="sort-fill"
                            style={{
                              opacity: 0.3,
                            }}
                          />
                        )}
                      </span>
                    </button>
                  </DataTableRow>

                  <DataTableRow size="md">
                    <button
                      onClick={() => onSortChange("address")}
                      className="sub-text font-bold cursor-pointer w-full text-start flex justify-between"
                    >
                      <span>Address</span>{" "}
                      <span>
                        {sort.column === "address" ? (
                          sort.direction === "asc" ? (
                            <Icon name={"sort-down-fill"} />
                          ) : (
                            <Icon name={"sort-up-fill"} />
                          )
                        ) : (
                          <Icon
                            name="sort-fill"
                            style={{
                              opacity: 0.3,
                            }}
                          />
                        )}
                      </span>
                    </button>
                  </DataTableRow>

                  <DataTableRow size="sm">
                    <button
                      onClick={() => onSortChange("email")}
                      className="sub-text font-bold cursor-pointer w-full text-start flex justify-between"
                    >
                      <span>Email</span>{" "}
                      <span>
                        {sort.column === "email" ? (
                          sort.direction === "asc" ? (
                            <Icon name={"sort-down-fill"} />
                          ) : (
                            <Icon name={"sort-up-fill"} />
                          )
                        ) : (
                          <Icon
                            name="sort-fill"
                            style={{
                              opacity: 0.3,
                            }}
                          />
                        )}
                      </span>
                    </button>
                  </DataTableRow>

                  <DataTableRow size="md">
                    <button
                      onClick={() => onSortChange("phone")}
                      className="sub-text font-bold cursor-pointer w-full text-start flex justify-between"
                    >
                      <span>Phone</span>{" "}
                      <span>
                        {sort.column === "phone" ? (
                          sort.direction === "asc" ? (
                            <Icon name={"sort-down-fill"} />
                          ) : (
                            <Icon name={"sort-up-fill"} />
                          )
                        ) : (
                          <Icon
                            name="sort-fill"
                            style={{
                              opacity: 0.3,
                            }}
                          />
                        )}
                      </span>
                    </button>
                  </DataTableRow>
				  <DataTableRow size="md">
					 <button
                      onClick={() => onSortChange("alt_email")}
                      className="sub-text font-bold cursor-pointer w-full text-start flex justify-between"
                    >
                      <span>Referal Code</span>{" "}
                      <span>
                        {sort.column === "alt_email" ? (
                          sort.direction === "asc" ? (
                            <Icon name={"sort-down-fill"} />
                          ) : (
                            <Icon name={"sort-up-fill"} />
                          )
                        ) : (
                          <Icon
                            name="sort-fill"
                            style={{
                              opacity: 0.3,
                            }}
                          />
                        )}
                      </span>
                    </button>
                  </DataTableRow>

                  <DataTableRow size="md">
                    <button
                      onClick={() => onSortChange("updated_at")}
                      className="sub-text font-bold cursor-pointer w-full text-start flex justify-between"
                    >
                      <span>Last Updated</span>
                      <span>
                        {sort.column === "updated_at" ? (
                          sort.direction === "asc" ? (
                            <Icon name={"sort-down-fill"} />
                          ) : (
                            <Icon name={"sort-up-fill"} />
                          )
                        ) : (
                          <Icon
                            name="sort-fill"
                            style={{
                              opacity: 0.3,
                            }}
                          />
                        )}
                      </span>
                    </button>
                  </DataTableRow>


                  <DataTableRow size="md">
                    <button
                      onClick={() => onSortChange("status")}
                      className="sub-text font-bold cursor-pointer w-full text-start flex justify-between"
                    >
                      <span>Status</span>{" "}
                      <span>
                        {sort.column === "status" &&
                          (sort.direction === "asc" ? (
                            <Icon name={"sort-down-fill"} />
                          ) : (
                            <Icon name={"sort-up-fill"} />
                          ))}
                      </span>
                    </button>
                  </DataTableRow>
                  
                  <DataTableRow className="nk-tb-col-tools text-end">
                    <span className="sub-text font-bold">Action</span>
                  </DataTableRow>
                </DataTableHead>
                {patients?.data?.length > 0
                  ? patients?.data?.map((item) => {
                      return (
                        <DataTableItem key={item?.id}>
                          <DataTableRow>
                            <div className="user-card">
                              <UserAvatar
                                theme={item?.avatarBg}
                                className="xs"
                                text={findUpper(item?.name?.toUpperCase())}
                                aimage={item?.image}
                              ></UserAvatar>
                              <div className="user-info">
                                <span className="tb-lead">{item?.name}</span>
                              </div>
                            </div>
                          </DataTableRow>
                          <DataTableRow size="md">
                            <span>{item?.address || "-"}</span>
                          </DataTableRow>
                          <DataTableRow size="sm">
                            <span>{item?.email}</span>
                          </DataTableRow>
                          <DataTableRow size="md">
                            <span>{item?.phone}</span>
                          </DataTableRow>
						  <DataTableRow size="md">
                            <span>{item?.alt_email}</span>
                          </DataTableRow>
                          <DataTableRow size="md">
                            <span>
                              {item?.updated_at
                                ? new Date(item.updated_at).toLocaleDateString()
                                : "-"}
                            </span>
                          </DataTableRow>

                          <DataTableRow size="md">
                            <button
                              onClick={() => {
                                setModal((prev) => ({ ...prev, status: true }));
                                setSelectedUser(item);
                              }}
                              className={`${item?.status ? "bg-green-500" : "bg-red-500"} p-1 rounded-lg text-white`}
                            >
                              {item?.status ? "Active" : "Suspended"}
                            </button>
                          </DataTableRow>
                          <DataTableRow className="nk-tb-col-tools">
                            <ul className="nk-tb-actions gx-1">
                              <Link to={`/patients/edit/${item?.id}`} className="nk-tb-action-hidden">
                                <TooltipComponent
                                  tag="a"
                                  containerClassName="btn btn-trigger btn-icon"
                                  id={"edit" + item.id}
                                  icon="edit-alt-fill"
                                  direction="top"
                                  text="Edit"
                                />
                              </Link>
                              <li
                                className="nk-tb-action-hidden"
                                onClick={() => {
                                  setModal((prev) => ({ ...prev, delete: true }));
                                  setSelectedUser(item);
                                }}
                              >
                                <TooltipComponent
                                  tag="a"
                                  containerClassName="btn btn-trigger btn-icon"
                                  id={"delete" + item?.id}
                                  icon="trash-fill"
                                  direction="top"
                                  text="Delete"
                                />
                              </li>
                              <li>
                                <UncontrolledDropdown>
                                  <DropdownToggle tag="a" className="dropdown-toggle btn btn-icon btn-trigger">
                                    <Icon name="more-h"></Icon>
                                  </DropdownToggle>
                                  <DropdownMenu end>
                                    <ul className="link-list-opt no-bdr">
                                      <Link to={`/patients/edit/${item?.id}`} className="p-0">
                                        <DropdownItem tag="a">
                                          <Icon name="edit"></Icon>
                                          <span>Edit</span>
                                        </DropdownItem>
                                      </Link>
                                      <li className="cursor-pointer">
                                        <DropdownItem
                                          tag="a"
                                          onClick={(ev) => {
                                            setModal((prev) => ({ ...prev, delete: true }));
                                            setSelectedUser(item);
                                          }}
                                        >
                                          <Icon name="trash"></Icon>
                                          <span>Delete</span>
                                        </DropdownItem>
                                      </li>
                                    </ul>
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </li>
                            </ul>
                          </DataTableRow>
                        </DataTableItem>
                      );
                    })
                  : null}
              </DataTableBody>
              <div className="card-inner">
                {patients?.data?.length > 0 ? (
                  <PaginationComponent
                    itemPerPage={itemPerPage}
                    totalItems={patients?.meta?.total}
                    paginate={paginate}
                    currentPage={currentPage}
                    setItemPerPage={setItemPerPage}
                  />
                ) : (
                  <div className="text-center">
                    <span className="text-silent">No data found</span>
                  </div>
                )}
              </div>
            </DataTable>
          </Block>
        )}
      </Content>

      <Modal isOpen={modal.delete} className="modal-dialog-centered" size="sm">
        <ConfirmDelete onDelete={handleDelete} onClose={() => setModal({ delete: false })} isLoading={isLoading} />
      </Modal>

      <Modal isOpen={modal.status} className="modal-dialog-centered" size="sm">
        <ConfirmChangeStatus
          onChange={handleChangeStatus}
          onClose={() => setModal({ status: false })}
          isLoading={isLoading}
        />
      </Modal>
    </React.Fragment>
  );
};

export default AllPatients;
